import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {AppContext} from '../../../App'
import AddAlertIcon from '../../../assets/icons/AddAlertIcon'
import {ListItemIcon, ListItemText} from '@mui/material'
import {addUserAlert, setAlertModalData} from '../../../reducer/alerts.reducer'
import {
    createUserAlertFromSnapshot,
    firstIndicatorOutputCodeForAlertUi,
    getCreateUserAlertRequestObject,
    getOperatorSource,
    getUserAlertMessageFromCondition,
    resolveDrawingAlertModalPayload
} from '../../../util/alerts'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import {useUser} from '../../../context/UserContext'
import {FeatureCategory, FeatureSubCategory} from '../../adminUserRestrictions/Restrictions.constants'
import {Group, setFeatureRestrictionDialog} from '../../../reducer/user.reducer'
import { FeatureCode } from '../../../util/RestrictionFeatureCode'
import { createAlert } from '../../../services/apis/alerts'
import commonActions from '../../../actions/common.actions'
import { selectIndicators } from '../../../selectors/chart'
import { SeriesGroup } from '../chart.typings'
import { SeriesGroupCategory } from '../../../pages/home'
import { getBaseSecurityKeyFromComparableToken, getSecurityAsync, getSecurityKey } from '../../../util/security'
import { convertMapToKeyValueArray } from '../../../util/common'
import { AlertExpression_Type, UserAlert } from '../../../proto/generated/nexus'
import type { ChartContextMenuSource } from './ChartContextMenuHost'
import { Security } from '../../../proto/generated/security'
import store, { RootState } from '../../../store'
import { buildScreenerAlertChartSnapshot } from '../../../util/conditionalEntry'
import { getScreenerContextMenuPrice } from '../../screener/screenerContextMenuUtil'

/** Chart alert snapshots use display price (same as chart candle close), not native LTP. */
function resolveScreenerAlertLastPriceDisplay(
    security: Security,
    customPriceValue?: string,
    liveLtpNative?: number
): number {
    if (customPriceValue) {
        const parsed = Number(customPriceValue)
        if (!Number.isNaN(parsed) && parsed > 0) return parsed
    }
    const multiplier = security.priceMultiplier ?? 100
    const key = getSecurityKey(security.exchange, security.code)
    const snapLtp = store.getState().securityStore.snapshots[key]?.lastTradedPrice
    if (snapLtp && snapLtp > 0) return snapLtp / multiplier
    if (liveLtpNative && liveLtpNative > 0) return liveLtpNative / multiplier
    const display = getScreenerContextMenuPrice(security)
    const fromDisplay = Number(display)
    if (!Number.isNaN(fromDisplay) && fromDisplay > 0) return fromDisplay
    return 0
}

interface AlertMenuItemProps {
    viewKey: string
    disabled?: boolean
    customPriceValue?: string
    handleCloseMenu: Function
    symbolName: string | undefined
    seriesGroup?: SeriesGroup
    menuSource?: ChartContextMenuSource
    security?: Security
    /** Parent MenuItem invokes this so the full row is clickable (not only icon/text). */
    registerMenuAction?: (fn: (() => void) | undefined) => void
}

function AlertContextMenuItem(props: AlertMenuItemProps) {
    const dispatch = useDispatch()
    const indicators = useSelector(selectIndicators)
    const userAlerts = useSelector((state: RootState) => state.alertsStore.alerts)
    const {
        viewKey,
        customPriceValue,
        handleCloseMenu,
        symbolName,
        seriesGroup,
        menuSource = 'chart',
        security,
        registerMenuAction
    } = props
    const appContext = useContext(AppContext)
    const {user: {hasAccess}} = useUser()
    const securityKey = security ? getSecurityKey(security.exchange, security.code) : ''
    const liveSnapshot = useSelector((state: RootState) =>
        securityKey ? state.securityStore.snapshots[securityKey] : undefined
    )
    const chartSnapshot = useMemo(()=>{
        if(appContext?.getChartSnapshot?.[viewKey]) {
            return (appContext.getChartSnapshot[viewKey] as any)()
        }
        return undefined
    }, [appContext.getChartSnapshot, viewKey])
    const [price, setPrice] = useState(customPriceValue !== undefined ? Number(customPriceValue) : undefined)

    useEffect(()=>{
        if (menuSource === 'screener') return
        if(chartSnapshot && !customPriceValue) {
            setPrice(chartSnapshot.data.close)
        }
    }, [chartSnapshot, customPriceValue, menuSource])

    const adaptAlertForPaneSeriesGroup = useCallback(async (baseAlert: UserAlert): Promise<UserAlert> => {
        if (!seriesGroup || seriesGroup.primary) return baseAlert
        const sac = baseAlert.condition?.securityAlertCondition
        if (!sac?.lhsExpression) return baseAlert

        if (seriesGroup.category === SeriesGroupCategory.SERIES_VOLUME) {
            const lhs = sac.lhsExpression
            const nextLhs = lhs.type === AlertExpression_Type.SPREAD_SECURITY
                ? {
                    ...lhs,
                    spreadSecurity: lhs.spreadSecurity ? { ...lhs.spreadSecurity, source: 'volume' } : lhs.spreadSecurity
                }
                : lhs.type === AlertExpression_Type.SIMPLE_SECURITY
                  ? {
                      ...lhs,
                      simpleSecurity: lhs.simpleSecurity ? { ...lhs.simpleSecurity, source: 'volume' } : lhs.simpleSecurity
                  }
                  : lhs
            return {
                ...baseAlert,
                condition: {
                    ...baseAlert.condition,
                    securityAlertCondition: { ...sac, lhsExpression: nextLhs }
                }
            }
        }

        if (seriesGroup.category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) {
            const cmpToken = seriesGroup.securityList?.[0]
            if (!cmpToken) return baseAlert
            const baseKey = getBaseSecurityKeyFromComparableToken(cmpToken) || cmpToken
            const sec = await getSecurityAsync(baseKey)
            if (!sec) return baseAlert
            const lhsExpression = {
                type: AlertExpression_Type.SIMPLE_SECURITY,
                simpleSecurity: {
                    security: { exchange: sec.exchange, code: sec.code },
                    source: getOperatorSource(sac.operator)
                },
                timeBucket: sac.lhsExpression.timeBucket,
                indicator: undefined,
                fixedValue: undefined,
                spreadSecurity: undefined
            }
            return {
                ...baseAlert,
                security: { exchange: sec.exchange, code: sec.code },
                condition: {
                    ...baseAlert.condition,
                    securityAlertCondition: { ...sac, lhsExpression }
                }
            }
        }

        if (seriesGroup.category === SeriesGroupCategory.SERIES_INDICATOR) {
            const indicatorDef = indicators.find((i) => i.code === seriesGroup.scriptCode)
            const output =
                firstIndicatorOutputCodeForAlertUi(indicatorDef?.outputs) ||
                Object.values(seriesGroup.outputValues || {})[0]?.code ||
                ''
            if (!output) return baseAlert
            const lhsExpression = {
                type: AlertExpression_Type.TECHNICAL_INDICATOR,
                indicator: {
                    indicatorCode: seriesGroup.scriptCode,
                    params: convertMapToKeyValueArray(seriesGroup.inputValues || {}),
                    output,
                    expression: sac.lhsExpression
                },
                timeBucket: undefined,
                simpleSecurity: undefined,
                spreadSecurity: undefined,
                fixedValue: undefined
            }
            return {
                ...baseAlert,
                condition: {
                    ...baseAlert.condition,
                    securityAlertCondition: { ...sac, lhsExpression }
                }
            }
        }

        return baseAlert
    }, [seriesGroup, indicators])

    const openScreenerAlertDialog = useCallback(async () => {
        const stub = security
        if (!stub?.code || stub.exchange === undefined) return
        const fullSec = await getSecurityAsync(getSecurityKey(stub.exchange, stub.code))
        if (!fullSec) return

        const lastVolume = liveSnapshot?.volume ?? 0
        const lastPrice = resolveScreenerAlertLastPriceDisplay(
            fullSec,
            customPriceValue,
            liveSnapshot?.lastTradedPrice
        )
        const chartFn = appContext.getChartSnapshot?.[viewKey]
        const chartSnap = typeof chartFn === 'function' ? chartFn() : null
        const chartSnapshot = buildScreenerAlertChartSnapshot(
            fullSec,
            lastPrice,
            lastVolume,
            chartSnap ?? undefined
        )
        const { alert: draftAlert, edit, sourceDrawing } = resolveDrawingAlertModalPayload(
            userAlerts,
            undefined,
            chartSnapshot
        )
        const generatedMessage = await getUserAlertMessageFromCondition(
            draftAlert.condition,
            symbolName || fullSec.symbol || fullSec.code,
            indicators,
            {
                chartSnapshot,
                chartTimeBucket: chartSnapshot.timeBucket ?? draftAlert.timeBucket
            }
        )
        const alert: UserAlert = {
            ...draftAlert,
            security: { exchange: fullSec.exchange, code: fullSec.code },
            message: generatedMessage || draftAlert.message
        }
        dispatch(
            setAlertModalData({
                chartSnapshot,
                alert,
                edit,
                sourceDrawing
            })
        )
        handleCloseMenu()
    }, [
        security,
        liveSnapshot,
        customPriceValue,
        appContext,
        viewKey,
        userAlerts,
        symbolName,
        indicators,
        dispatch,
        handleCloseMenu
    ])

    const handleButtonClick = useCallback(async () => {
        if(!hasAccess(FeatureCategory.CHART, FeatureSubCategory.ALERTS)) {
            dispatch(setFeatureRestrictionDialog({
                open: true,
                feature: "Alerts",
                group: Group.CHART,
                featureRestrictionCode:FeatureCode.CHART_ALERTS
            }))
            handleCloseMenu()
            return
        }

        if (menuSource === 'screener') {
            AnalyticsManager.logEvent('Add Alert Clicked', {source: 'stock-screener'})
            try {
                await openScreenerAlertDialog()
            } catch (e: any) {
                commonActions.setApiError(dispatch, {
                    error: true,
                    message: e?.message || 'Failed to open alert dialog',
                    errorType: 'error'
                })
                handleCloseMenu()
            }
            return
        }

        AnalyticsManager.logEvent('Add Alert Clicked', {source: 'chart-grid'})
        const chartSnapshotMethod = appContext.getChartSnapshot[viewKey]
        const snapshot = chartSnapshotMethod ? chartSnapshotMethod() : null
        if (!snapshot) return
        if(price !== undefined) snapshot.data.close = Number(price)
        try {
            const baseAlert = await adaptAlertForPaneSeriesGroup(createUserAlertFromSnapshot(snapshot))
            const primary = snapshot.securityList?.[0]
            const alertSecurity = primary
                ? { exchange: primary.exchange, code: primary.code }
                : baseAlert.security
            const generatedMessage = await getUserAlertMessageFromCondition(
                baseAlert.condition,
                symbolName || primary?.code || '',
                indicators,
                {
                    chartSnapshot: snapshot,
                    chartTimeBucket: snapshot.timeBucket ?? baseAlert.timeBucket,
                }
            )
            const alert = {
                ...baseAlert,
                security: alertSecurity,
                message: generatedMessage || baseAlert.message
            }
            const chart = appContext.getChart(viewKey)
            const created = await createAlert(
                getCreateUserAlertRequestObject(alert, {
                    seriesGroups: chart?.chartConfig.seriesGroups,
                    indicators,
                })
            )
            dispatch(addUserAlert(created))
        } catch (e: any) {
            commonActions.setApiError(dispatch, {
                error: true,
                message: e?.message || 'Failed to create alert',
                errorType: 'error'
            })
        }
        handleCloseMenu()
    }, [appContext.getChartSnapshot, viewKey, price, dispatch, handleCloseMenu, hasAccess, symbolName, indicators, adaptAlertForPaneSeriesGroup, menuSource, openScreenerAlertDialog])

    useEffect(() => {
        if (!registerMenuAction) return
        registerMenuAction(() => {
            void handleButtonClick()
        })
        return () => registerMenuAction(undefined)
    }, [registerMenuAction, handleButtonClick])

    const displayPrice =
        menuSource === 'screener'
            ? undefined
            : customPriceValue ?? (price !== undefined ? String(price) : undefined)
    const alertLabelSuffix = displayPrice ? ` at ${displayPrice}` : ''

    return (
        <>
            <ListItemIcon>
                <AddAlertIcon sx={{fontSize: '1.5rem !important'}}/>
            </ListItemIcon>
            <ListItemText>
                Add Alert on {symbolName ?? ''}{alertLabelSuffix}
            </ListItemText>
        </>
    )
}

export default React.memo(AlertContextMenuItem)
